import React, { useEffect, useState } from 'react';
import { useNavigate } from 'src/utils/use-navigate';
import { Checkbox } from 'antd';
import './ml-recommendation-modal.scss';
import AILogo from '../../../../src/assets/svg/ai.svg?react';
import IpControlIcon from '../../../../src/assets/svg/ip-control.svg?react';
import AppsIcon from '../../../../src/assets/svg/apps-logo.svg?react';
import GenericModal from '@src/components/shared/generic-modal/generic-modal';
import StopPolicyRecommendationsModal from './stop-policy-recommendations-modal';
import { useStores } from '@src/components/store';
import usePromiseFeedback from '../../shared/promise-feedback/usePromiseFeedback';
import mlRecommendationService from '@src/services/ml-recommendation.service';
import { EventType } from '@src/dtos/analytics.dto';
import { TooltipDisableReason } from '@src/components/shared/tooltip-disable-message/tooltip-disable-reason';
import {observer} from 'mobx-react-lite';
import { PENDING_DISABLE_REASON } from '@src/utils/constants';

const ML_RECOMMENDATIONS_MODAL = 'mlRecommendationsModal';

interface MlRecommendationModalProps {
    onClose: () => void;
    hasDraft: boolean;
    connectionName?: string;
}

const MlRecommendationModal: React.FC<MlRecommendationModalProps> = observer(({
    onClose,
    hasDraft,
    connectionName
}) => {

    const navigate = useNavigate();
    const [selectedApps, setSelectedApps] = useState<string[]>([]);
    const [selectedIps, setSelectedIps] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showStopModal, setShowStopModal] = useState(false);

    const { fwVersionStore, customerStore } = useStores();
    const [promiseFeedbackFactory] = usePromiseFeedback();

    const getModalDisabledState= () => {
        if (fwVersionStore.mlRecommendationData?.recommendationWasApplied) {
            return { disabled:true, disabledReason: 'You have already applied this recommendation' };
        }
        if (fwVersionStore.pendingVersion) {
            return { disabled: true, disabledReason: PENDING_DISABLE_REASON };
        }
        return { disabled:false, disabledReason:undefined };
    };

    const { disabled, disabledReason } = getModalDisabledState();

    useEffect(() => {
        fwVersionStore.sendGeneralEvent(EventType.ModalView, { modalName: ML_RECOMMENDATIONS_MODAL });
    }, []);

    const applicationsToBlock = fwVersionStore.mlRecommendationData?.applicationsToBlock ?? [];
    const ipsToBlock = fwVersionStore.mlRecommendationData?.ipsToBlock ?? [];

    const allAppsSelected = selectedApps.length === applicationsToBlock.length;
    const allIpsSelected = selectedIps.length === ipsToBlock.length;

    useEffect(() => {
        if (disabled) return;

        if (fwVersionStore.mlRecommendationData?.applicationsToBlock) {
            setSelectedApps(fwVersionStore.mlRecommendationData.applicationsToBlock);
        }
        if (fwVersionStore.mlRecommendationData?.ipsToBlock) {
            setSelectedIps(fwVersionStore.mlRecommendationData.ipsToBlock);
        }

    }, [fwVersionStore.mlRecommendationData, disabled]);

    const handleSelectAllApps = () => {
        setSelectedApps(allAppsSelected ? [] : applicationsToBlock);
    };

    const handleSelectAllIps = () => {
        setSelectedIps(allIpsSelected ? [] : ipsToBlock);
    };

    const handleAppToggle = (app: string, checked: boolean) => {
        setSelectedApps((prev) =>
            checked ? [...prev, app] : prev.filter((a) => a !== app)
        );
    };

    const handleIpToggle = (ip: string, checked: boolean) => {
        setSelectedIps((prev) =>
            checked ? [...prev, ip] : prev.filter((i) => i !== ip)
        );
    };

    const updatePolicy = () => {
        fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: hasDraft ? 'updateDraft' : 'createDraft', location: 'mlRecommendationsModal' });
        setIsLoading(true);
        promiseFeedbackFactory({
            promise: mlRecommendationService.applyMlRecommendation(
                customerStore.customerId,
                fwVersionStore.mlRecommendationData?.serviceId ?? 0,
                {
                    ipsToBlock: selectedIps,
                    applicationsToBlock: selectedApps
                }
            ),
            successCallback: (res) => {
                if (!hasDraft && !connectionName) {
                    const versionPath = `version/${res.externalId}/${fwVersionStore.versionProtectionPath}`;
                    navigate(`/${customerStore.customerId}/${customerStore.serviceId}/protection/${versionPath}`, { replace: true });
                }
                setIsLoading(false);
                onClose();
            },
            errorCallback: () => {
                setIsLoading(false);
            },
            notifObj: { show: true, notificationMsg: 'Draft updated with policy recommendations' }
        });
    };

    const body = <div className="ml-recommendation-modal-content">
        {connectionName && <span className="connection-name">{connectionName}</span>}
        <div className="ml-recommendation-modal-subtitle">
            After analyzing your network traffic, we recommend to block the following to enhance your business protection
        </div>
        <div className="recommended-app-wrapper">
            {!!applicationsToBlock.length && <div className="recommended-app-items">
                <div className="recommended-list-title">
                    <span className="icon-wrapper"><AppsIcon /></span>
                    {`Block ${applicationsToBlock.length} Apps`}
                </div>
                <div className="recommended-list">
                    <div className="recommended-list-item">
                        <TooltipDisableReason disableReason={disabledReason} placement="bottomLeft">
                            <Checkbox checked={allAppsSelected} onChange={handleSelectAllApps} disabled={disabled}>Select
                                all</Checkbox>
                        </TooltipDisableReason>
                    </div>
                    {applicationsToBlock.map(app => (
                        <div key={app} className="recommended-list-item">
                            <TooltipDisableReason disableReason={disabledReason} placement="bottomLeft">
                                <Checkbox
                                    checked={selectedApps.includes(app)}
                                    onChange={e => handleAppToggle(app, e.target.checked)}
                                    disabled={disabled}
                                >
                                    {app}
                                </Checkbox>
                            </TooltipDisableReason>
                        </div>
                    ))}
                </div>
            </div>}
            {!!ipsToBlock.length && <div className="recommended-app-items">
                <div className="recommended-list-title">
                    <span className="icon-wrapper"><IpControlIcon /></span>
                    {`Block ${ipsToBlock.length} IPs`}
                </div>
                <div className="recommended-list">
                    <div className="recommended-list-item">
                        <TooltipDisableReason disableReason={disabledReason} placement="bottomLeft">
                            <Checkbox checked={allIpsSelected} onChange={handleSelectAllIps} disabled={disabled}>Select
                                all</Checkbox>
                        </TooltipDisableReason>
                    </div>
                    {ipsToBlock.map(ip => (
                        <div key={ip} className="recommended-list-item">
                            <TooltipDisableReason disableReason={disabledReason} placement="bottomLeft">
                                <Checkbox
                                    checked={selectedIps.includes(ip)}
                                    onChange={e => handleIpToggle(ip, e.target.checked)}
                                    disabled={disabled}
                                >
                                    {ip}
                                </Checkbox>
                            </TooltipDisableReason>
                        </div>
                    ))}
                </div>
            </div>}
        </div>
    </div>;

    const actionButtons = [
        {
            text: 'Stop sending policy recommendations',
            onClick: () => {
                fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'stopSendingPolicyRecommendations', location: ML_RECOMMENDATIONS_MODAL });
                setShowStopModal(true);
            },
            className: 'secondary-btn',
        },
        {
            text: `${hasDraft ? 'Update' : 'Create'} Draft`,
            onClick: updatePolicy,
            className: `primary-btn ${isLoading ? 'loading' : ''}`,
            disabled: (!selectedApps.length && !selectedIps.length) || isLoading || disabled,
            disableReason: disabledReason
        },
    ];

    return (
        <>
            <GenericModal
                title={
                    <>
                        <AILogo />
                        AI-powered policy recommendations
                    </>
                }
                actionButtons={actionButtons}
                open={!showStopModal}
                onCancel={onClose}
                wrapClassName="ml-recommendation-modal"
                width={1024}
                closable={true}
                body={body}
            />
            {showStopModal && (
                <StopPolicyRecommendationsModal
                    open={showStopModal}
                    onClose={() => {
                        setShowStopModal(false);
                        onClose();
                    }}
                />
            )}
        </>
    );
});

export default MlRecommendationModal;
